import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import {
  FactorBoxData,
  FactorGridProps,
  FactorLensFocus,
} from '@/interfaces/factorLens.interface'
import { FactorLevelGroup } from '@/services/report-dashboard/raDashboard.data.service'
import useElementDimensions from '@/utils/useElementDimensions'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { isEqual } from 'lodash'
import React, { useMemo } from 'react'

const FactorLensGrid = ({
  gridData,
  cardState,
  setSelected,
  colors,
}: FactorGridProps) => {

  const theme = useTheme()

  const scaleFactor = 0.95
  const padding = 75
  const boxAspect = 3 / 2 //3:2, w:h

  const { dimensions, dimensionsRef } = useElementDimensions()
  const { width, height } = dimensions ?? {}

  const amountOfFactors = Object.keys(gridData?.levels?.[2] ?? {}).length

  const amountOfTiers = 3

  const [oldBoxWidth, setOldBoxWidth] = React.useState(100)

  const widthLimited = (width: number, height: number) => {
    let gridAspect = (amountOfFactors * boxAspect) / amountOfTiers;
    let aspectAvailable = width / height;

    return gridAspect > aspectAvailable;
  }

  const boxWidth = useMemo(() => {
    let newWidth = oldBoxWidth
    if (width != undefined && height != undefined) {
      if (widthLimited(width, height)) {
        newWidth = ((width - padding) / amountOfFactors) * scaleFactor
      } else {
        newWidth =
          ((height - padding) / amountOfTiers) * boxAspect * scaleFactor
      }
    }

    setOldBoxWidth(newWidth)
    return newWidth
  }, [width, height, amountOfFactors])

  const drawFactorBox = (level: number, group: FactorLevelGroup, totalRisk: number, barColorIndex?: number) => {

    let factorLensFocus : FactorLensFocus = {level: level, name: group.name}

    return (
      <FactorBox
        key={group.name}
        width={boxWidth}
        aspect={boxAspect}
        selected={isEqual(cardState?.selectedFactorBox, factorLensFocus)}
        setAsSelected={() => setSelected(factorLensFocus)}
        factorName={group.name}
        factorContrib={group.riskContrib}
        factorWeight={group.riskContrib / totalRisk}
        colors={colors}
        barColorIndex={barColorIndex}
      />
    )
  }

  // Level 0 holds the single "Total Risk" box, which every tier's percentage is
  // scaled against. When a selection has no factor data the level buckets come
  // back empty, so guard on the actual Total Risk element (not just `levels`)
  // before dereferencing it — otherwise reading `.riskContrib` on undefined
  // crashes the whole dashboard.
  const totalRiskGroup = gridData?.levels?.[0]?.[0]
  const hasFactorData =
    !!totalRiskGroup &&
    Array.isArray(gridData?.levels?.[1]) &&
    Array.isArray(gridData?.levels?.[2])

  return (
    <div
      ref={dimensionsRef}
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 4,
        alignContent: 'center',
      }}
    >
      {hasFactorData ? (
        <div>
          <Stack
            direction='row'
            sx={{ justifyContent: 'center', m: "8px" }}
            spacing={"8px"}
          >
            {/* Top level is just one item; Total Risk */}
            {drawFactorBox(0, totalRiskGroup, totalRiskGroup.riskContrib)}
          </Stack>
          <Stack
            direction='row'
            sx={{ justifyContent: 'center', m: "8px" }}
            spacing={"8px"}
          >
            {(gridData?.levels?.[1] ?? []).map((group) => (
              drawFactorBox(1, group, totalRiskGroup.riskContrib)
            ))}
          </Stack>
          <Stack
            direction='row'
            sx={{ justifyContent: 'center', m: "8px" }}
            spacing={"8px"}
          >
            {(gridData?.levels?.[2] ?? []).map((group, i) => (
              drawFactorBox(2, group, totalRiskGroup.riskContrib, i)
            ))}
          </Stack>
        </div>
      ) : (
        <Stack
          sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography
            sx={{ color: theme.factorLensGridPrimaryColor, opacity: 0.7 }}
          >
            No factor data available
          </Typography>
        </Stack>
      )}
    </div>
  )
}

const FactorBox: React.FC<FactorBoxData> = (data: FactorBoxData) => {
  const theme = useTheme();

  const shouldShowPercentage = data.factorName !== 'Total Risk';

  const isTotal = data.factorName === 'Total Risk';
  const isSpecific = data.factorName === 'Specific Risk';

  const backgroundColor = isTotal
    ? data.colors.totalColor
    : isSpecific
      ? data.colors.specificColor
      : theme.factorLensGridBoxColor;

  const borderColor = isTotal
    ? data.colors.totalBorder
    : isSpecific
      ? data.colors.specificBorder
      : theme.factorLensGridBoxBorder;

  const textColor = isTotal
    ? data.colors.totalSelected
    : isSpecific
      ? data.colors.specificSelected
      : theme.factorLensGridPrimaryColor;

  const selectedColor = isTotal
    ? data.colors.totalSelected
    : isSpecific
      ? data.colors.specificSelected
      : theme.factorLensGridPrimaryColor;

  const isNegative = data.factorContrib < 0;

  const percentageBarColor = isSpecific
      ? selectedColor
      : data.barColorIndex !== undefined
        ? data.colors.barColors[data.barColorIndex % data.colors.barColors.length]
        : theme.factorLensGridPrimaryColor;

  const boxDefaultStyle = {
    width: data.width,
    height: data.width / data.aspect,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    backgroundColor,
    borderRadius: data.width * 0.1 + 'px',
    border: `1px solid ${borderColor}`,
    color: textColor,
    boxShadow: 1,
    cursor: 'pointer'
  }

  const boxSelectedStyle = {
    boxShadow: `inset 0 0 0 2px ${selectedColor}`,
  }

  const boxStyle = data.selected
    ? { ...boxDefaultStyle, ...boxSelectedStyle }
    : boxDefaultStyle

  let percentage = data.factorWeight * 100;

  return (
    <Box sx={boxStyle} onClick={data.setAsSelected}>
      <Stack justifyContent={'center'}>
        <Typography align={'center'} sx={{ fontSize: data.width * 0.1 }}>
          {data.factorName}
        </Typography>
        <Typography
          align={'center'}
          sx={{ fontSize: data.width * 0.15, fontWeight: 600 }}
        >
          {data.factorContrib?.toFixed(2)}
        </Typography>

        {percentage != null && shouldShowPercentage && (
          <Stack
            direction='row'
            sx={{ alignItems: 'center', justifyContent: 'center' }}
            spacing={1}
          >
            <div
              style={{
                height: 5,
                width: data.width / 3,
                borderRadius: 90,
                border: '1px solid #5554',
                display: 'flex',
                justifyContent: isNegative ? 'flex-end' : 'flex-start',
                overflow: 'hidden',
              }}
            >
              <div
              style={{
                height: '100%',
                width: (Math.abs(percentage)) + '%',
                borderRadius: 90,
                backgroundColor: percentageBarColor,
              }}
            ></div>
            </div>
            <Typography sx={{ fontSize: data.width * 0.1, color: 'grey' }}>
              {percentage.toFixed(2)}%
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

export default FactorLensGrid
